// Workflow domain re-exports + lightweight CEL pretty-printer.
//
// The full builder lives in `lib/workflows/builder.ts`. This file is
// the small surface the rest of the app reads: the Workflow type plus
// a `formatExpr` helper that turns CEL into a one-liner for list /
// sidebar rows. It deliberately doesn't try to parse CEL — that's the
// builder's job and the server's `POST /workflows/parse-expr` is the
// canonical answer. We use simple regex shortcuts so the common
// builder-emitted shapes render as natural language; everything else
// shows the raw expression in monospace.

export type { Workflow } from "@/lib/api-types";

// formatExpr returns a pretty label for a workflow's CEL expression.
// The `isStructured` flag tells the caller whether the result is safe
// to render as text (true) or should be wrapped in <code> (false).
export function formatExpr(expr: string): {
  pretty: string;
  isStructured: boolean;
} {
  const trimmed = expr.trim();
  if (!trimmed) {
    return { pretty: "(empty)", isStructured: true };
  }
  const pretty = prettifyCEL(trimmed);
  return { pretty, isStructured: pretty !== trimmed };
}

// prettifyCEL applies a tiny set of cosmetic substitutions over the
// most common builder-emitted shapes. Anything we don't recognise
// falls through unchanged — the caller renders it as code.
//
// Kept as plain string substitutions because the goal is "make the
// expression scannable in a table row", not "parse CEL". The full
// parse happens server-side.
function prettifyCEL(expr: string): string {
  let s = expr;

  // Strip the `has(x) && x <op> N` guard the legacy migration
  // emits; the guard is implementation noise, not signal.
  s = s.replace(
    /has\(shipment\.(weight_lbs|longest_dimension_inches)\) && shipment\.\1 /g,
    "shipment.$1 ",
  );

  // Operator words.
  s = s.replace(/ == /g, " is ");
  s = s.replace(/ != /g, " is not ");
  s = s.replace(/ <= /g, " ≤ ");
  s = s.replace(/ >= /g, " ≥ ");
  s = s.replace(/ < /g, " < ");
  s = s.replace(/ > /g, " > ");
  s = s.replace(/ && /g, " AND ");
  s = s.replace(/ \|\| /g, " OR ");

  // Field labels — order matters: longer paths first so we don't
  // partially match the shorter ones.
  const fieldLabels: [RegExp, string][] = [
    [/shipment\.longest_dimension_inches/g, "Longest dimension"],
    [/shipment\.weight_lbs/g, "Weight"],
    [/shipment\.tier_preference/g, "Tier"],
    [/shipment\.is_hazmat/g, "Hazmat"],
    [/shipment\.is_fragile/g, "Fragile"],
    [/shipment\.requires_team_lift/g, "Team lift"],
    [/shipment\.requirements/g, "Requirements"],
    [/order\.requested_method/g, "Method"],
    [/order\.customer_id/g, "Customer"],
  ];
  for (const [re, label] of fieldLabels) {
    s = s.replace(re, label);
  }

  return s;
}
